<!-- DynamicFilterModal.vue -->
<template>
  <b-modal
    v-model="modalShow"
    size="xl"
    title="Filtros Avançados"
    :no-enforce-focus="true"
    :no-close-on-backdrop="true"
  >
    <div class="row">
      <div v-for="field in visibleFields" :key="field.key" :class="field.col_class || 'col-12 col-md-4'" class="mt-2">
        <label class="font-weight-bold d-block">{{ field.label }}</label>

        <!-- TEXT -->
        <input v-if="field.component === 'text'" v-model="form[field.key]" class="form-control" type="text" />

        <!-- SELECT -->
        <select v-else-if="field.component === 'select'" v-model="form[field.key]" class="form-control">
          <option value="">Todos</option>
          <option v-for="opt in optionsFor(field)" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>

        <!-- MULTISELECT -->
        <multiselect
          v-else-if="field.component === 'multiselect'"
          v-model="form[field.key]"
          :options="optionsFor(field)"
          :multiple="field.multiple"
          label="name"
          track-by="id"
          :placeholder="field.label"
          :searchable="true"
          :loading="loading"
          :close-on-select="false"
        />

        <!-- DATE RANGE -->
        <div v-else-if="field.component === 'date-range'" class="d-flex gap-2">
          <date-picker v-model="form[field.key + '_start']" format="DD/MM/YYYY" placeholder="De" class="flex-fill" />
          <date-picker v-model="form[field.key + '_end']" format="DD/MM/YYYY" placeholder="Até" class="flex-fill" />
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="modalShow = false">Fechar</b-button>
      <b-button variant="outline-danger" @click="clear">Limpar</b-button>
      <b-button variant="primary" @click="apply">Aplicar Filtro</b-button>
    </template>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { mapState, mapActions } from 'vuex'
import { states } from '@/pages/participants/statesData'
import service from '@/pages/settings/services'

export default {
  components: { Multiselect, DatePicker },
  props: {
    context: { type: String, required: true }
  },
  data () {
    return {
      modalShow: false,
      fields: [],
      form: {},
      statesList: states || [],
      loading: false
    }
  },
  computed: {
    ...mapState('tag', ['tags']),
    ...mapState('settings', ['statusList', 'refstatusList']),
    ...mapState('group', ['groups']),

    visibleFields () {
      return this.fields.filter(f => {
        if (!f.show_when) return true
        const { field, is } = f.show_when || {}
        return String(this.form[field]) === String(is)
      })
    }
  },
  methods: {
    ...mapActions('settings', { GetStatus: 'GetStatusList', GetRefStatus: 'GetRefStatusList' }),
    ...mapActions('tag', { getTags: 'getAll' }),
    ...mapActions('group', { getGroups: 'getAll' }),

    open () {
      this.modalShow = true

      // Recarrega a config a cada abertura: filtros habilitados/alterados em
      // Configurações passam a aparecer sem precisar renavegar (antes só
      // carregava na 1ª vez e a lista ficava defasada). Requests leves, e o
      // formulário preenchido (form) é preservado.
      this.loadEverything()
    },

    async loadEverything () {
      this.loading = true
      try {
        await this.loadConfig()
        await Promise.all([
          this.getGroups({ limit: 1000, status: 1 }),
          this.getTags({ limit: 1000 }),
          this.GetStatus(),
          this.GetRefStatus()
        ])
      } catch (err) {
        this.$toast.error('Erro ao carregar filtros')
      } finally {
        this.loading = false
      }
    },

    async loadConfig () {
      const { data } = await service.filters(this.context)
      this.fields = data || []
      this.initFormIfEmpty()
    },

    // Só cria o form vazio na primeira vez
    initFormIfEmpty () {
      if (Object.keys(this.form).length === 0) {
        const form = {}
        this.fields.forEach(f => {
          if (f.component === 'date-range') {
            form[f.key + '_start'] = null
            form[f.key + '_end'] = null
          } else if (f.component === 'multiselect') {
            form[f.key] = []
          } else {
            form[f.key] = ''
          }
        })
        this.form = form
      }
    },

    optionsFor (field) {
      if (field.static_options) return field.static_options

      if (field.key === 'status') {
        return (this.statusList || []).map((label, i) => ({ value: String(i), label }))
      }
      if (field.key === 'ref_status') {
        // id/name pro multiselect (label="name" track-by="id"); value/label mantidos por compat
        const obj = this.refstatusList || {}
        return Object.keys(obj).map(k => ({ id: k, name: obj[k], value: k, label: obj[k] }))
      }
      if (field.key === 'group_id' || field.key === 'groups') {
        return (this.groups || []).map(g => ({ id: g.id, name: g.name }))
      }
      if (field.key === 'tags') {
        return (this.tags || []).map(t => ({ id: t.id, name: t.name }))
      }
      if (field.source === 'static:states') {
        return this.statesList.map(s => ({ value: s.sigla || s.value, label: s.value || s.label }))
      }
      return []
    },

    clear () {
      this.fields.forEach(f => {
        if (f.component === 'date-range') {
          this.form[f.key + '_start'] = null
          this.form[f.key + '_end'] = null
        } else if (f.component === 'multiselect') {
          this.form[f.key] = []
        } else {
          this.form[f.key] = ''
        }
      })
      this.apply()
    },

    apply () {
      const payload = {}

      Object.keys(this.form).forEach(key => {
        const value = this.form[key]
        if (value === '' || value === null || (Array.isArray(value) && value.length === 0)) {
          return
        }
        if (Array.isArray(value)) {
          payload[key] = value.map(item => item.id || item.value)
        } else if (key.includes('_start') || key.includes('_end')) {
          payload[key] = value
        } else {
          payload[key] = value
        }
      })

      this.$emit('filtered', payload)
      this.modalShow = false
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss" scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
